/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-throw-literal */
import { Auth0Client } from "@auth0/auth0-spa-js";
import * as Sentry from "@sentry/react";
import axios from "axios";
import { startCase } from "lodash";
import { apiConfig, auth0Config } from "../config";
import { EncountersQuery } from "../hooks/useEncounters";
import { Allergy } from "../types/allergy";
import { CodeableConcept } from "../types/codeableConcept";
import { TCopilot } from "../types/copilot";
import { MedicalConfiguration } from "../types/MedicalConfiguration";
import { EncounterType, MedicalEncounter } from "../types/medicalEncounter";
import { MedicationAdministration } from "../types/medicationAdminstration";
import { MedicationRequest } from "../types/medicationRequest";
import { MedicationStatement } from "../types/medicationStatement";
import { Observation } from "../types/observation";
import { Paginated } from "../types/pagination";
import { Patient } from "../types/patient";
import { Reference } from "../types/reference";
import createFormData from "../utils/createFormData";
import querySearchEndpoint from "../utils/querySearchEndpoint";
import { EncounterTemplate } from "../types/templates";
import {
  getAuthConfigWithProps,
  getAuthHeader,
  getBasicAuthConfig,
} from "./authUtils";

const allergies = "allergy-intolerances";
const encounters = "encounters";
const conditions = "conditions";
const patientHistory = "patient-history";
const familyMemberHistory = "family-member-history";
const clinicalImpressions = "clinical-impressions";
const medicationStatements = "medication-statements";
const copilots = "copilots";

export const medicalApiPaths = {
  medicationStatement: "medication-statements",
  compositions: "compositions",
  clinicalImpressions: "clinical-impressions",
  observations: "observations",
  procedures: "procedures",
  questionnaire: "questionnaire",
  questionnaireResponse: "questionnaire-response",
  conditions: "conditions",
  carePlan: "care-plan",
  flag: "flag",
  immunization: "immunization",
  encounters: "encounters",
  allergies: "allergy-intolerances",
  medicationRequest: "medication-requests",
  copilot: "copilots",
  medicalConfigurations: "medical-configurations",
  templates: "templates",
  riskAssessment: "risk-assessments",
} as const;

export type TMedicalApiPaths =
  (typeof medicalApiPaths)[keyof typeof medicalApiPaths];

class MedicalsApi {
  private auth0Client: Auth0Client | null = null;

  constructor() {
    this.auth0Client = new Auth0Client({
      redirect_uri: window.location.origin,
      ...auth0Config,
    });
  }

  async findClinicalImpressions(query: any) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const url = new URL(
        `${apiConfig.medicalsServiceBase}/${clinicalImpressions}`,
      );
      return await querySearchEndpoint(url, query, config);
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        return [];
      }
      console.error(`Error when retrieving compositions ${JSON.stringify(e)}`);
      throw new Error(`An error occured. Please try again.`);
    }
  }

  // TODO: Remove this method it's not used
  async getClinicalImpressionById(id: string) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.get(
        `${apiConfig.medicalsServiceBase}/${clinicalImpressions}/${id}`,
        config,
      );
      return response.data;
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        return {};
      }
      console.error(
        `Error when retrieving clinical impression with id: ${id}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      throw new Error(`An error occured. Please try again.`);
    }
  }

  // TODO: Remove this method it's not used
  async getEncounterAnalytics(organizationId: string, query: any) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const url = new URL(
        `${apiConfig.medicalsServiceBase}/${encounters}/analytics/${organizationId}`,
      );

      const response = querySearchEndpoint(url, query, config);

      return await response;
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        return {};
      }
      console.error(
        `Error when retrieving encounter analytics with organizationId: ${organizationId}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async getImmunizationHistory(query: any) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);

      const url = new URL(
        `${apiConfig.medicalsServiceBase}/${medicalApiPaths.immunization}`,
      );

      return await querySearchEndpoint(
        url,
        { ...query, status: ["completed", "not-done"] },
        config,
      );
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        throw {
          status: 403,
          message: "Insufficient Permissions - Cannot Get Immunization History",
        };
      }

      console.error(
        `Error getting immunizations for patient. Error: ${JSON.stringify(e)}`,
      );

      throw new Error(`An error occured. Please try again.`);
    }
  }

  // TODO: Remove this method it's not used
  async updateClinicalImpression(
    id: string,
    formData: any,
    headers: { [key: string]: any },
  ) {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = {
        headers: { ...authHeaders.headers, ...headers },
      };
      const response = await axios.put(
        `${apiConfig.medicalsServiceBase}/${clinicalImpressions}/${id}`,
        formData,
        config,
      );
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        throw {
          status: 403,
          message:
            "Insufficient Permissions - Cannot Update Clinical Impression",
        };
      }

      console.error(
        `Error updating clinical impression  with ID ${id}. Error: ${JSON.stringify(
          e,
        )}`,
      );

      throw new Error(`An error occured. Please try again.`);
    }
  }

  async getPatientHistory(query: any) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const url = new URL(`${apiConfig.medicalsServiceBase}/${patientHistory}`);
      const response = await querySearchEndpoint(url, query, config);
      return response;
    } catch (e) {
      console.error(`Error when retrieving compositions ${JSON.stringify(e)}`);
      throw new Error(`An error occurred. Please try again.`);
    }
  }

  async updateFamilyHistory(
    id: string,
    formData: any,
    headers: { [key: string]: any },
  ) {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = {
        headers: { ...authHeaders.headers, ...headers },
      };
      const response = await axios.put(
        `${apiConfig.medicalsServiceBase}/${familyMemberHistory}/${id}`,
        formData,
        config,
      );
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        throw {
          status: 403,
          message:
            "Insufficient Permissions - Cannot Update Family History Record",
        };
      }
      console.error(
        `Error updating family member information with ID ${id}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async saveFamilyHistory(formData: any, headers: { [key: string]: any }) {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = {
        headers: { ...authHeaders.headers, ...headers },
      };
      const response = await axios.post(
        `${apiConfig.medicalsServiceBase}/${familyMemberHistory}`,
        formData,
        config,
      );
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        throw {
          status: 403,
          message: "Insufficient Permissions - Cannot Create Family History",
        };
      }
      console.error(`Error saving family history. Error: ${JSON.stringify(e)}`);
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async findActiveAlerts(subjectReference: any) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const url = new URL(
        `${apiConfig.medicalsServiceBase}/${medicalApiPaths.flag}/active/by-subject`,
      );
      url.searchParams.append("subject", subjectReference);
      const response = await axios.get(url.toString(), config);
      return response?.data || [];
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        return [];
      }
      console.error(
        `Error when retrieving active alerts for ${subjectReference} ${JSON.stringify(
          e,
        )}`,
      );
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async findConditions(query: any) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const url = new URL(`${apiConfig.medicalsServiceBase}/${conditions}`);
      return await querySearchEndpoint(url, query, config);
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        return [];
      }
      console.error(`Error when retrieving conditions. ${JSON.stringify(e)}`);
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async findMedicationRequests(
    query: any,
    signal?: AbortSignal,
  ): Promise<MedicationRequest[]> {
    try {
      const config = await getAuthConfigWithProps(
        this.auth0Client,
        signal ? { signal } : {},
      );
      const url = new URL(
        `${apiConfig.medicalsServiceBase}/medication-requests`,
      );

      return await querySearchEndpoint(url, query, config);
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        return [];
      }
      console.error(
        `Error when retrieving medication requests. ${JSON.stringify(e)}`,
      );
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async findMedicationRequestsByEncounter(
    query: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<Paginated<EncounterType>> {
    const config = await getAuthConfigWithProps(
      this.auth0Client,
      signal ? { signal } : {},
    );
    const url = new URL(
      `${apiConfig.medicalsServiceBase}/medication-requests/encounters`,
    );

    return querySearchEndpoint(url, query, config);
  }

  async findMedicationAdministrations(
    query: any,
  ): Promise<MedicationAdministration[]> {
    try {
      const config = await getAuthHeader(this.auth0Client);
      const url = new URL(
        `${apiConfig.medicalsServiceBase}/medication-administrations`,
      );
      return await querySearchEndpoint(url, query, config);
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        return [];
      }
      console.error(`Error when retrieving conditions. ${JSON.stringify(e)}`);
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async findMedicationStatements(query: any): Promise<MedicationStatement[]> {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const url = new URL(
        `${apiConfig.medicalsServiceBase}/${medicationStatements}`,
      );
      return await querySearchEndpoint(url, query, config);
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        // return status code for notification
        return [];
      }
      console.error(`Error when retrieving conditions. ${JSON.stringify(e)}`);
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async findMedicationStatementsHistory(query: {
    encounter: string;
    patient: string;
    status: string | string[];
    date: Date;
    limit: number;
  }) {
    try {
      const config = await getAuthHeader(this.auth0Client);
      const url = new URL(
        `${apiConfig.medicalsServiceBase}/${medicationStatements}/history`,
      );

      return await querySearchEndpoint(url, query, config);
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        // return status code for notification
        return [];
      }
      console.error(`Error when retrieving conditions. ${JSON.stringify(e)}`);
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async getEntityById(
    id: string,
    type: TMedicalApiPaths,
    query?: { [key: string]: any },
  ) {
    try {
      const config = await getAuthConfigWithProps(this.auth0Client, {
        params: query,
      });

      const response = await axios.get(
        `${apiConfig.medicalsServiceBase}/${type}/${id}`,
        config,
      );

      return response.data;
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        return {};
      }
      console.error(
        `Error when retrieving ${type} with id: ${id}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async getObservationsByPatientId(
    id: string,
    codes?: string[],
    start?: string,
    end?: string,
    additionalParams?: Record<string, unknown>,
  ) {
    try {
      const config = await getAuthConfigWithProps(this.auth0Client, {
        params: {
          patient: id,
          limit: 50,
          code: codes,
          start,
          end,
          ...additionalParams,
        },
      });

      const response = await axios.get(
        `${apiConfig.medicalsServiceBase}/observations`,
        config,
      );

      return response.data;
    } catch (err) {
      if (err?.response?.data?.statusCode === 403) {
        return {};
      }

      console.error(
        `Error when retrieving vitals with patient id: ${id}. Error: ${JSON.stringify(
          err,
        )}`,
      );

      throw new Error(`An error occured. Please try again.`);
    }
  }

  async getVitalsByObservationId(id: string) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.get(
        `${apiConfig.medicalsServiceBase}/observations/${id}/vitals`,
        config,
      );
      return response.data;
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        return {};
      }
      console.error(
        `Error when retrieving vitals with observation id: ${id}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      throw new Error(`An error occured. Please try again.`);
    }
  }

  /**
   * This endpoint is implemented as a POST request to handle large payloads that exceed GET request limits.
   * @note This endpoint is specifically designed for bulk observation retrieval
   * and should not be used for general purpose observation updates or queries.
   */
  async getObservationsForEncounters(
    encounterIds: string[],
    query: EncountersQuery = {},
    signal?: AbortSignal,
  ) {
    try {
      const config = await getAuthConfigWithProps(
        this.auth0Client,
        signal ? { signal } : {},
      );

      const response = await axios.post(
        `${apiConfig.medicalsServiceBase}/encounters/observations`,
        {
          encounterIds,
          "full-text-search": query["full-text-search"] ?? "",
          "exclude-status": query["exclude-status"] ?? "",
        },
        { ...config, signal },
      );
      return response.data;
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        return {};
      }
      console.error(
        `Error when retrieving observations for encounters. ${JSON.stringify(e)}`,
      );
      throw new Error(`An error occurred. Please try again.`);
    }
  }

  async getEncounterStatusTally(organizationId: string) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.get(
        `${apiConfig.medicalsServiceBase}/encounters/stats/by-status/${organizationId}`,
        config,
      );
      return response.data;
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        return {};
      }
      console.error(
        `Error when retrieving Encounter Tally by status for ${organizationId}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async findQuestionnaireResponse(
    code: string,
    searchParams: { [key: string]: any },
    signal?: AbortSignal,
  ) {
    try {
      const config = await getAuthConfigWithProps(
        this.auth0Client,
        signal ? { signal } : {},
      );

      const response = await axios.get(
        `${apiConfig.medicalsServiceBase}/questionnaire-response`,
        {
          ...config,
          params: { code, ...searchParams },
        },
      );

      return response.data ?? [];
    } catch (error) {
      if (error?.response?.data?.statusCode === 403) {
        return {};
      }

      throw new Error(`An error occurred. Please try again.`);
    }
  }

  async findQuestionnaireResponseForPatient(
    patientId: string,
    questionnaire: string,
    encounterId?: string,
  ) {
    try {
      let response;

      const config = await getBasicAuthConfig(this.auth0Client);

      if (!encounterId) {
        response = await axios.get(
          `${apiConfig.medicalsServiceBase}/questionnaire-response/patient/${patientId}/questionnaire/${questionnaire}`,
          config,
        );
      } else {
        response = await axios.get(
          `${apiConfig.medicalsServiceBase}/questionnaire-response/patient/${patientId}/encounter/${encounterId}/questionnaire/${questionnaire}`,
          config,
        );
      }

      return response.data;
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        return {};
      }
      console.error(
        `Error when retrieving questionnaire with patientID: ${patientId} and questionnaire code ${questionnaire}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async findEntities(query: any, type: TMedicalApiPaths, signal?: AbortSignal) {
    try {
      const config = await getAuthConfigWithProps(
        this.auth0Client,
        signal ? { signal } : {},
      );
      const url = new URL(`${apiConfig.medicalsServiceBase}/${type}`);

      return await querySearchEndpoint(url, query, config);
    } catch (e: any) {
      if (e?.response?.data?.statusCode === 403) {
        return [];
      }
      console.error("Error when retrieving compositions: ", JSON.stringify(e));

      throw new Error(
        `An error occurred while retrieving entities of type ${type}. Please try again.`,
      );
    }
  }

  async countEntities(
    query: any,
    type: TMedicalApiPaths,
    signal?: AbortSignal,
  ) {
    try {
      const config = await getAuthConfigWithProps(
        this.auth0Client,
        signal ? { signal } : {},
      );
      const url = new URL(
        `${apiConfig.medicalsServiceBase}/${type}/stats/count`,
      );

      const response = querySearchEndpoint(url, query, config);
      return await response;
    } catch (e) {
      console.error(
        `Error when retrieving total number of appointments ${JSON.stringify(
          e,
        )}`,
      );
      throw e;
    }
  }

  async setPatientAllergies(
    allergies: {
      allergy: CodeableConcept;
      reaction?: CodeableConcept;
    }[],
    patient: string,
    encounter: string,
    recorder: any,
  ) {
    const config = await getBasicAuthConfig(this.auth0Client);
    const response = await axios.post(
      `${apiConfig.medicalsServiceBase}/${medicalApiPaths.allergies}/batch-set`,
      {
        allergies,
        patient,
        encounter,
        recorder,
      },
      config,
    );

    if (!response.data) throw new Error("An error occured");
    if (response.data.statusCode === 403) {
      throw {
        status: 403,
        message: `Insufficient Permissions - Cannot set patient allergies`,
      };
    }

    return response.data;
  }

  async setPatientMedications(
    medications: CodeableConcept[],
    patient: string,
    encounter: string,
    recorder: any,
  ) {
    const config = await getBasicAuthConfig(this.auth0Client);
    const response = await axios.post(
      `${apiConfig.medicalsServiceBase}/${medicalApiPaths.medicationStatement}/batch-set`,
      {
        medications,
        patient,
        encounter,
        recorder,
      },
      config,
    );

    if (!response.data) throw new Error("An error occured");
    if (response.data.statusCode === 403) {
      throw {
        status: 403,
        message: `Insufficient Permissions - Cannot set patient allergies`,
      };
    }

    return response.data;
  }

  async setPatientMedicalHistory(
    conditions: any[],
    patient: string,
    encounter: string,
    recorder: any,
  ) {
    const config = await getBasicAuthConfig(this.auth0Client);
    const response = await axios.post(
      `${apiConfig.medicalsServiceBase}/${medicalApiPaths.conditions}/batch-set`,
      {
        conditions,
        patient,
        encounter,
        recorder,
      },
      config,
    );

    if (!response.data) throw new Error("An error occured");
    if (response.data.statusCode === 403) {
      throw {
        status: 403,
        message: `Insufficient Permissions - Cannot set patient allergies`,
      };
    }

    return response.data;
  }

  async updateEntity(
    id: string,
    formData: any,
    type: TMedicalApiPaths,
    headers?: { [key: string]: any },
  ) {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = {
        headers: { ...authHeaders.headers, ...headers },
      };

      const response = await axios.put(
        `${apiConfig.medicalsServiceBase}/${type}/${id}`,
        formData,
        config,
      );
      if (!response.data) throw new Error("An error occured");

      return response.data;
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        throw {
          status: 403,
          message: `Insufficient Permissions - Cannot update ${startCase(
            type,
          )}`,
        };
      }

      console.error(
        `Error updating ${startCase(
          type,
        )} with ID ${id}. Error: ${JSON.stringify(e)}`,
      );

      throw new Error(`An error occured. Please try again.`);
    }
  }

  async createEntity(
    formData: any,
    type: TMedicalApiPaths,
    headers?: { [key: string]: any },
  ) {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = {
        headers: { ...authHeaders.headers, ...headers },
      };
      const response = await axios.post(
        `${apiConfig.medicalsServiceBase}/${type}`,
        formData,
        config,
      );
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        throw {
          status: 403,
          message: `Insufficient Permissions - Cannot create new ${startCase(
            type,
          )}`,
        };
      }
      const serverMessage = e?.response?.data?.message;
      console.error(
        `Error updating ${startCase(type)}. Error: ${JSON.stringify(e)}`,
      );
      throw new Error(
        typeof serverMessage === "string" && serverMessage
          ? serverMessage
          : `An error occured. Please try again.`,
      );
    }
  }

  async getConditionById(id: string) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.get(
        `${apiConfig.medicalsServiceBase}/${conditions}/${id}`,
        config,
      );
      return response.data;
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        return {};
      }
      console.error(
        `Error when retrieving condition with id: ${id}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async saveCondition(formData: any, headers: { [key: string]: any }) {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = {
        headers: { ...authHeaders.headers, ...headers },
      };
      const response = await axios.post(
        `${apiConfig.medicalsServiceBase}/${conditions}`,
        formData,
        config,
      );
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        throw {
          status: 403,
          message: "Insufficient Permissions - Cannot Create New Condition",
        };
      }
      console.error(`Error when adding new conditon data ${JSON.stringify(e)}`);
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async updateCondition(
    id: string,
    formData: any,
    headers: { [key: string]: any },
  ) {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = {
        headers: { ...authHeaders.headers, ...headers },
      };
      const response = await axios.put(
        `${apiConfig.medicalsServiceBase}/${conditions}/${id}`,
        formData,
        config,
      );
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        throw {
          status: 403,
          message: "Insufficient Permissions - Cannot Update Condition",
        };
      }
      console.error(
        `Error updating condition information with ID ${id}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async findAllergies(query: any, signal?: AbortSignal): Promise<Allergy[]> {
    try {
      const config = await getAuthConfigWithProps(
        this.auth0Client,
        signal ? { signal } : {},
      );
      const url = new URL(`${apiConfig.medicalsServiceBase}/${allergies}`);
      return await querySearchEndpoint(url, query, config);
    } catch (e) {
      if (
        e?.response?.data?.statusCode === 403 ||
        e?.name === "CanceledError"
      ) {
        return [];
      }
      console.error(`Error when retrieving allergies ${JSON.stringify(e)}`);
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async saveAllergy(formData: any, headers: { [key: string]: any }) {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = {
        headers: { ...authHeaders.headers, ...headers },
      };
      const response = await axios.post(
        `${apiConfig.medicalsServiceBase}/${allergies}`,
        formData,
        config,
      );
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        throw {
          status: 403,
          message:
            "Insufficient Permissions - Cannot Create New Allergy Record",
        };
      }
      console.error(`Error when adding new allergy data ${JSON.stringify(e)}`);
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async updateAllergy(
    id: string,
    formData: any,
    headers: { [key: string]: any },
  ) {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = {
        headers: { ...authHeaders.headers, ...headers },
      };
      const response = await axios.put(
        `${apiConfig.medicalsServiceBase}/${allergies}/${id}`,
        formData,
        config,
      );
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        throw {
          status: 403,
          message: "Insufficient Permissions - Cannot Update Allergy Record",
        };
      }
      console.error(
        `Error updating allergy information with ID ${id}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async getEncounteryId(
    encounterId: string,
    organization: { id: string; authId: string },
  ) {
    if (!organization?.id || !organization?.authId) {
      throw new Error("Organization is required to retrieve an encounter.");
    }

    try {
      const config = await getAuthHeader(this.auth0Client, {
        "x-organization-authid": organization.authId,
        organization: `Organization/${organization.id}`,
      });
      const response = await axios.get(
        `${apiConfig.medicalsServiceBase}/${encounters}/${encounterId}`,
        config,
      );
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      console.error(
        `Error when retrieving encounter with id: ${encounterId}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      if (e.response?.data?.statusCode === 403) {
        throw new Error(
          "Access Denied: You do not have enough permissions to view the this item.",
        );
      }

      throw new Error(`An error occured. Please try again.`);
    }
  }

  async findEncounters(
    query: any,
    signal?: AbortSignal,
  ): Promise<MedicalEncounter[]> {
    try {
      const config = await getAuthConfigWithProps(
        this.auth0Client,
        signal ? { signal } : {},
      );
      const url = new URL(`${apiConfig.medicalsServiceBase}/encounters`);

      return await querySearchEndpoint(url, query, config);
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        return [];
      }
      console.error(`Error when retrieving encounters ${JSON.stringify(e)}`);
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async saveEncounter(
    formData: any,
    headers: { [key: string]: any },
    files?: File[],
  ) {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = {
        headers: {
          ...authHeaders.headers,
          "Content-Type": "multipart/form-data",
          ...headers,
        },
        params: {
          skipDuplicate: true,
        },
      };

      const bodyFormData = createFormData(formData, files);

      const response = await axios.post(
        `${apiConfig.medicalsServiceBase}/encounters`,
        bodyFormData,
        config,
      );

      if (response.data) {
        return response.data;
      }
    } catch (e) {
      if (e.response.status === 409) {
        // This indicates an encounter already existing
        const { data } = e.response;
        const { encounter } = data;

        throw {
          status: 409,
          message: "There is an ongoing encounter for this patient",
          encounter,
        };
      }

      if (e?.response?.data?.statusCode === 403) {
        throw {
          status: 403,
          message: "Insufficient Permissions - Cannot Create New Encounter",
        };
      }

      console.error(
        `Error when adding new encounter data ${JSON.stringify(e)}`,
      );
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async updateEncounter(
    id: string,
    formData: any,
    headers?: { [key: string]: any },
  ) {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = {
        headers: { ...authHeaders.headers, ...headers },
      };
      const response = await axios.put(
        `${apiConfig.medicalsServiceBase}/encounters/${id}`,
        formData,
        config,
      );
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        throw {
          status: 403,
          message: "Insufficient Permissions - Cannot Update Encounter",
        };
      }
      console.error(
        `Error updating encounter information with ID ${id}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async getEncounterCarePlan(encounterId: string) {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = { headers: { ...authHeaders.headers } };
      const response = await axios.get(
        `${apiConfig.medicalsServiceBase}/${medicalApiPaths.carePlan}/encounter/${encounterId}`,
        config,
      );
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        throw {
          status: 403,
          message:
            "Insufficient Permissions - Cannot revrieve careplan for this encounter",
        };
      }
      console.error(
        `Error revrieveng care plan for encounter with id ${encounterId}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async updateEncounterProperties(
    id: string,
    formData: any,
    headers: { [key: string]: any },
  ) {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = {
        headers: { ...authHeaders.headers, ...headers },
      };
      const response = await axios.put(
        `${apiConfig.medicalsServiceBase}/encounters/partial/${id}`,
        formData,
        config,
      );
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        throw {
          status: 403,
          message:
            "Insufficient Permissions - Cannot Update Encounter Properties",
        };
      }
      console.error(
        `Error updating encounter information with ID ${id}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async finalizeEncounter(id: string, headers: { [key: string]: any }) {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = {
        headers: { ...authHeaders.headers, ...headers },
      };

      const response = await axios.post(
        `${apiConfig.medicalsServiceBase}/encounters/finalize/${id}`,
        {},
        config,
      );

      if (response.data) {
        return response.data;
      }
    } catch (e) {
      console.error(
        `Error finalizing encounter with ID ${id}. Error: ${JSON.stringify(e)}`,
      );
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async printEntities(
    ids: string[],
    organizationId: string,
    authId: string,
    type: TMedicalApiPaths,
    img?: string,
    options?: {
      responseType?: string;
      method?: "window" | "download";
    },
    queryParams?: { [key: string]: any },
  ) {
    const authHeaders = await getBasicAuthConfig(this.auth0Client);
    // set option defaults
    options = options ?? {
      responseType: "pdf",
      method: "window",
    };
    options.method = options.method ?? "window";
    options.responseType = options.responseType ?? "pdf";

    const url = new URL(`${apiConfig.medicalsServiceBase}/${type}/print`);

    if (organizationId) url.searchParams.append("o", organizationId);
    if (options.responseType) {
      url.searchParams.append("response-type", options?.responseType);
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const id of ids) {
      url.searchParams.append("requests", id);
    }

    const response = await axios.get<Blob>(url.toString(), {
      headers: {
        ...authHeaders.headers,
        "x-organization-authid": authId,
        organization: `Organization/${organizationId}`,
      },
      params: queryParams,
      responseType: "blob",
    });

    if (
      options.responseType === "pdf" &&
      response.headers["content-type"] !== "application/pdf"
    ) {
      throw new Error("PDF file(s) could not be retrieved");
    }

    // file display logic moved to component
    return response.data;
  }

  async findFirstEncounterForPatient(
    patientId: string,
    organizationId: string,
  ) {
    const authHeaders = await getBasicAuthConfig(this.auth0Client);
    const config = { headers: { ...authHeaders.headers } };
    const response = await axios.get(
      `${apiConfig.medicalsServiceBase}/encounters/patients/${patientId}/organizations/${organizationId}/first`,
      config,
    );

    return response.data;
  }

  async findCopilots(
    query?: {
      custodian?: string;
      patient?: string | string[];
      status?: string | string[];
      "has-encounter"?: boolean | string;
      /**
       * When set, clears encounter on notes whose linked encounter status
       * is in this comma-separated list (or `true` → entered-in-error).
       */
      "omit-encounter-statuses"?: boolean | string;
      "full-text-search"?: string;
      limit?: number;
      start?: number;
      paginated?: boolean;
      sort?: string;
      order?: "asc" | "desc";
    },
    signal?: AbortSignal,
  ): Promise<Paginated<TCopilot> | TCopilot[]> {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = {
        headers: { ...authHeaders.headers },
        params: query,
        signal,
        // Repeat array keys (status=a&status=b) so Nest receives real arrays.
        // Default axios indexing can drop/mis-parse multi-status list filters.
        paramsSerializer: {
          indexes: null,
        },
      };
      const response = await axios.get(
        `${apiConfig.medicalsServiceBase}/${copilots}`,
        config,
      );
      return response.data;
    } catch (e) {
      console.error(
        `Error when querying copilots. Error: ${JSON.stringify(e)}`,
      );
      if (e.response?.data?.statusCode === 403) {
        throw new Error(
          "Access Denied: You do not have enough permissions to view AI notes.",
        );
      }
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async createCopilot(
    copilot: Partial<TCopilot>,
    headers: Record<string, unknown> = {},
  ): Promise<TCopilot> {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = {
        headers: { ...authHeaders.headers, ...headers },
      };
      const response = await axios.post(
        `${apiConfig.medicalsServiceBase}/${copilots}`,
        copilot,
        config,
      );
      // Same envelope unwrap as linkCopilotEncounter / updateCopilotStatus.
      return response.data?.data ?? response.data;
    } catch (e) {
      console.error(`Error when creating copilot. Error: ${JSON.stringify(e)}`);
      if (e.response?.data?.statusCode === 403) {
        throw new Error(
          "Access Denied: You do not have enough permissions to create AI notes.",
        );
      }
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async getCopilotById(copilotId: string): Promise<TCopilot | undefined> {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = { headers: { ...authHeaders.headers } };
      const response = await axios.get(
        `${apiConfig.medicalsServiceBase}/${copilots}/${copilotId}`,
        config,
      );
      return response.data;
    } catch (e) {
      console.error(
        `Error when retrieving copilot with ID: ${copilotId}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      if (e.response?.data?.statusCode === 403) {
        throw new Error(
          "Access Denied: You do not have enough permissions to view this item.",
        );
      }
      throw new Error(`An error occured. Please try again.`);
    }
  }

  async linkCopilotEncounter(
    copilotId: string,
    encounterId: string,
  ): Promise<TCopilot> {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = { headers: { ...authHeaders.headers } };
      const response = await axios.post(
        `${apiConfig.medicalsServiceBase}/${copilots}/${copilotId}/link-encounter`,
        { encounterId },
        config,
      );
      return response.data?.data ?? response.data;
    } catch (e) {
      console.error(
        `Error linking encounter to copilot ${copilotId}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      throw new Error(
        e?.response?.data?.message ||
          `An error occured while linking the encounter.`,
      );
    }
  }

  async updateCopilotStatus(
    copilotId: string,
    status: string,
  ): Promise<TCopilot> {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = { headers: { ...authHeaders.headers } };
      const response = await axios.patch(
        `${apiConfig.medicalsServiceBase}/${copilots}/${copilotId}/status`,
        { status },
        config,
      );
      return response.data?.data ?? response.data;
    } catch (e) {
      console.error(
        `Error updating copilot status ${copilotId}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      throw new Error(
        e?.response?.data?.message ||
          `An error occured while updating the AI note status.`,
      );
    }
  }

  async updateCopilotNote(
    copilotId: string,
    copilot: TCopilot,
    headers: Record<string, unknown>,
  ) {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = {
        headers: { ...authHeaders.headers, ...headers },
      };
      return await axios.put(
        `${apiConfig.medicalsServiceBase}/copilots/${copilotId}`,
        copilot,
        config,
      );
    } catch (e) {
      console.error(
        `Error when updating copilot note with ID: ${copilotId}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      if (e.response?.data?.statusCode === 403) {
        throw new Error(
          "Access Denied: You do not have enough permissions to edit this item.",
        );
      }

      throw new Error(`An error occured. Please try again.`);
    }
  }

  async getCopilotForEncounter(encounterId: string) {
    try {
      const authHeaders = await getBasicAuthConfig(this.auth0Client);
      const config = { headers: { ...authHeaders.headers } };
      const response = await axios.get(
        `${apiConfig.medicalsServiceBase}/copilots/by-encounter/${encounterId}`,
        config,
      );
      if (response.data) {
        return response.data;
      }
      return undefined;
    } catch (e) {
      console.error(
        `Error when retrieving copilot for encounter with ID: ${encounterId}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      if (e.response?.data?.statusCode === 403) {
        throw new Error(
          "Access Denied: You do not have enough permissions to view this item.",
        );
      }

      throw new Error(`An error occured. Please try again.`);
    }
  }

  async updateCopilotTranscript(
    transcript: string,
    encounterId: string,
    attester: Reference,
    dateTime: Date,
    headers: Record<string, unknown>,
  ): Promise<TCopilot> {
    const authHeaders = await getBasicAuthConfig(this.auth0Client);
    const config = {
      headers: { ...authHeaders.headers, ...headers },
    };

    return axios
      .put(
        `${apiConfig.medicalsServiceBase}/${copilots}/encounters/${encounterId}/transcript`,
        {
          transcript,
          attester,
          "date-time": dateTime,
        },
        config,
      )
      .then((res) => res.data.data);
  }

  // healthCheck
  async healthCheck() {
    try {
      const response = await axios.get(
        `${apiConfig.medicalsServiceBase}/health`,
      );
      return response.data;
    } catch (e) {
      console.error(
        `Medicals Service health check was unsuccessful${JSON.stringify(e)}`,
      );
    }
  }

  // Templates
  async getTemplates(
    query?: Record<string, string>,
  ): Promise<EncounterTemplate[]> {
    try {
      const config = await getAuthConfigWithProps(this.auth0Client, {
        params: query,
      });
      const response = await axios.get(
        `${apiConfig.medicalsServiceBase}/templates`,
        config,
      );
      if (response.data) {
        return response.data.data;
      }

      return [];
    } catch (e) {
      // Lacking the templates permission is an expected state for some roles,
      // so return an empty list rather than surfacing it as a failure.
      if (e?.response?.data?.statusCode === 403) {
        console.error("Insufficient Permissions - Cannot Read Templates");
        return [];
      }
      console.error(`Error retrieving templates. Error: ${JSON.stringify(e)}`);
      throw new Error(`An error occurred. Please try again.`);
    }
  }

  async checkTemplateExists(query: Record<string, string | string[]>) {
    const config = await getAuthConfigWithProps(this.auth0Client, {
      params: { ...query, "excluded-status": "entered-in-error" },
    });
    try {
      const response = await axios.get(
        `${apiConfig.medicalsServiceBase}/templates`,
        config,
      );

      const templateExists = response.data.total > 0;

      return templateExists;
    } catch (error) {
      console.error("Error checking if template exists", error);
      throw new Error("Failed to check template existence");
    }
  }

  async getTemplate(
    id: string,
    headers?: Record<string, string>,
  ): Promise<EncounterTemplate> {
    try {
      const config = await getAuthHeader(this.auth0Client, headers);
      const response = await axios.get(
        `${apiConfig.medicalsServiceBase}/templates/${id}`,
        config,
      );
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        throw {
          status: 403,
          message: "Insufficient Permissions - Cannot Read Template Properties",
        };
      }
      console.error(
        `Error retrieving template information with ID ${id}. Error: ${JSON.stringify(e)}`,
      );
      throw new Error(`An error occurred. Please try again.`);
    }
  }

  async createTemplate(templateData: any, headers: { [key: string]: any }) {
    try {
      const config = await getAuthHeader(this.auth0Client, headers);
      const response = await axios.post(
        `${apiConfig.medicalsServiceBase}/templates`,
        templateData, // The data to be sent in the request body
        config,
      );
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        throw {
          status: 403,
          message: "Insufficient Permissions - Cannot Create Template",
        };
      }
      console.error(`Error creating template. Error: ${JSON.stringify(e)}`);
      throw new Error("An error occurred. Please try again.");
    }
  }

  async updateTemplate(
    id: string,
    templateData: any,
    headers?: Record<string, string>,
  ): Promise<EncounterTemplate> {
    try {
      const config = await getAuthHeader(this.auth0Client, headers);
      const response = await axios.put(
        `${apiConfig.medicalsServiceBase}/templates/${id}`,
        templateData, // The data to be sent in the request body
        config,
      );
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        throw {
          status: 403,
          message: "Insufficient Permissions - Cannot Update Template",
        };
      }
      console.error(
        `Error updating template with ID ${id}. Error: ${JSON.stringify(e)}`,
      );
      throw new Error("An error occurred. Please try again.");
    }
  }

  // medical configuration
  async getMedicalConfigByOrganization(orgId: string) {
    try {
      const response = await this.findEntities(
        { organization: orgId },
        medicalApiPaths.medicalConfigurations,
      );
      if (response?.[0]?.id) {
        return response[0];
      }
      // take the first from the list of find entities
      const config: MedicalConfiguration = {
        defaultEncounterType: "full",
        simpleTemplateId: "",
        detailedTemplateId: "",
        vitalMeasurement: {
          height: "cm",
          weight: "lbs",
          temperature: "celcius",
        },
      };
      return config;
    } catch (e) {
      console.error("Failed to retrieve medical configs", e);
      throw e;
    }
  }
  // MEDICATION ADMINSTRATIONS

  async getMedicationAdministrations(
    query: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<MedicationAdministration[]> {
    const config = await getAuthConfigWithProps(
      this.auth0Client,
      signal ? { signal } : {},
    );
    const url = new URL(
      `${apiConfig.medicalsServiceBase}/medication-administrations`,
    );

    return querySearchEndpoint(url, query, config);
  }

  async getUpcomingMedications(
    signal?: AbortSignal,
    query?: Record<string, unknown>,
  ): Promise<MedicationAdministration[]> {
    const config = await getAuthConfigWithProps(
      this.auth0Client,
      signal ? { signal } : {},
    );
    const url = new URL(
      `${apiConfig.medicalsServiceBase}/medication-administrations/upcoming`,
    );

    return querySearchEndpoint(url, query, config);
  }

  async saveMedicationAdministration(
    formData: Partial<MedicationAdministration>,
    headers?: Record<string, unknown>,
  ) {
    const authHeaders = await getBasicAuthConfig(this.auth0Client);
    const config = {
      headers: { ...authHeaders.headers, ...headers },
    };

    const response = await axios.post(
      `${apiConfig.medicalsServiceBase}/medication-administrations`,
      formData,
      config,
    );
    return response.data;
  }

  async resumeMedicationAdministration(
    id: string,
    body: Partial<MedicationAdministration>,
    headers: Record<string, unknown> = {},
  ): Promise<MedicationAdministration> {
    const authHeaders = await getBasicAuthConfig(this.auth0Client);
    const config = {
      headers: { ...authHeaders.headers, ...headers },
    };
    const response = await axios.put(
      `${apiConfig.medicalsServiceBase}/medication-administrations/resume/${id}`,
      body,
      config,
    );
    return response.data;
  }

  async updateMedicationAdministration(
    id: string,
    body: Partial<MedicationAdministration>,
    headers: Record<string, unknown> = {},
  ): Promise<MedicationAdministration> {
    const authHeaders = await getBasicAuthConfig(this.auth0Client);
    const config = {
      headers: { ...authHeaders.headers, ...headers },
    };
    const response = await axios.put(
      `${apiConfig.medicalsServiceBase}/medication-administrations/${id}`,
      body,
      config,
    );
    return response.data;
  }

  // MEDICATION REQUESTS

  async updateMedicationRequest(
    id: string,
    body: MedicationRequest,
    headers: { [key: string]: any } = {},
  ): Promise<MedicationRequest> {
    const authHeaders = await getBasicAuthConfig(this.auth0Client);
    const config = {
      headers: { ...authHeaders.headers, ...headers },
    };
    const response = await axios.put(
      `${apiConfig.medicalsServiceBase}/medication-requests/${id}`,
      body,
      config,
    );
    return response.data;
  }

  // triaged board
  async getTriagedBoard(
    query: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<
    Paginated<
      MedicalEncounter & {
        patient: Patient;
        vitals: { vitals: Observation };
        triageLevel: number;
      }
    >
  > {
    const config = await getAuthConfigWithProps(
      this.auth0Client,
      signal ? { signal } : {},
    );
    const url = new URL(
      `${apiConfig.medicalsServiceBase}/encounters/triaged-board`,
    );

    return querySearchEndpoint(url, query, config);
  }

  async setPatientMedicationRequests(
    medicationRequests: MedicationRequest[],
    patient: string,
    encounter: string,
  ) {
    const config = await getBasicAuthConfig(this.auth0Client);
    const response = await axios.post(
      `${apiConfig.medicalsServiceBase}/${medicalApiPaths.medicationRequest}/batch-set`,
      {
        medicationRequests,
        patient,
        encounter,
      },
      config,
    );

    if (!response.data) throw new Error("An error occured");
    if (response.data.statusCode === 403) {
      throw {
        status: 403,
        message: `Insufficient Permissions - Cannot set patient medication requests`,
      };
    }

    return response.data;
  }

  async getAiScribeUsage(organizationId: string): Promise<{
    usedMinutes: number;
    limitMinutes: number | null;
    remainingMinutes: number | null;
    isUnlimited: boolean;
    isOverLimit: boolean;
  }> {
    const config = await getBasicAuthConfig(this.auth0Client);
    const url = `${apiConfig.medicalsServiceBase}/organizations/${organizationId}/ai-scribe-usage`;
    const { data } = await axios.get(url, config);
    return data;
  }

  async notifyUpcomingMedications() {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.post(
        `${apiConfig.medicalsServiceBase}/cron-job/notify-upcoming-medications`,
        {},
        config,
      );
      if (response?.data) {
        return response.data;
      }
    } catch (e) {
      console.error(
        "Error notifying upcoming medications. " + JSON.stringify(e),
      );
      throw new Error(`An error occured. Please try again.`);
    }
  }
}

export const medicalsApi = new MedicalsApi();
